import { CardElement } from '@stripe/react-stripe-js';
import React, { useState } from 'react';

import useCheckoutState from '../../../hooks/useCheckoutState';
import PaymentElement from '../PaymentElement';
import Label from './Label';
import ExpressCheckoutElement from '../ExpressCheckoutElement';
import getBillingDetailsFromFields from '../../../lib/getBillingDetailsFromFields';
import getFormFieldValues from '../../../lib/getFormFieldValues';
import clsx from 'clsx';

// Define createPaymentMethodStyle before it's used
const createPaymentMethodStyle = ({ padding, styles = {} }) => ({
  style: {
    base: {
      iconColor: '#c0b6ba',
      fontWeight: 400,
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
      fontSmoothing: 'antialiased',
      background: 'white',
      fontSize: '16px',
      lineHeight: '1.5',
      padding,
      '::placeholder': {
        color: '#c0b6ba',
      },
      '::focus-visible': {
        outline: 'none',
      },
      ...styles,
    },
  },
});

const PaymentMethods = () => {
  const { page, seller, isScreenshot } = useCheckoutState();

  const paymentMethods = page.paymentMethods.stripe;

  if (paymentMethods.multiple?.enabled === true) {
    return <MultiplePaymentMethods />;
  }

  if (!seller.paymentGateways?.stripe?.enableAppleGooglePay) {
    return <CardPaymentMethod />;
  }

  if (
    paymentMethods.card?.enabled === true &&
    paymentMethods.agpay?.enabled === true &&
    !isScreenshot
  ) {
    if (paymentMethods.agpay.mode === 'express') {
      return <CardPaymentMethod />;
    }

    return <CardAgpayPaymentMethod />;
  }

  if (paymentMethods.card?.enabled === true) {
    return <CardPaymentMethod />;
  }

  return null;
};

function CardAgpayPaymentMethod() {
  const { isMeasurer } = useCheckoutState();

  return (
    <>
      <div className="cp_payment-method cp_stripe-express-checkout-element-wrapper">
        {!isMeasurer && <ExpressCheckoutElement mode="default" />}
      </div>

      <div className="cp_payment-method-wrapper cp_stripe-card-element-wrapper">
        <CardPaymentMethod />
      </div>
    </>
  );
}

function CardPaymentMethod() {
  const { page, form, isTesting } = useCheckoutState();
  const [isCardElementReady, setIsCardElementReady] = useState(false);

  const { cardLabelText } = page;

  const { fields } = getFormFieldValues(form.fields, page.fields);
  const billingDetails = getBillingDetailsFromFields(fields);

  const hasPostalCodeInAddress = !!billingDetails?.address?.postal_code;
  const hidePostalCode = hasPostalCodeInAddress || !!isTesting;

  return (
    <>
      <Label className="cp_form-label cp_payment-method-label" required>
        {cardLabelText}
      </Label>

      <div className="cp_payment-method cp_stripe-card-element-container mt-1 relative">
        <CardElement
          onReady={() => setIsCardElementReady(true)}
          id={`stripe-card-element-${page._id}`}
          options={{
            ...createPaymentMethodStyle({
              styles: page.cardFieldStyles,
            }),
            hidePostalCode,
            disableLink: true,
          }}
          className={clsx(
            'cp_stripe-card-element bg-white placeholder-gray-200 border-none shadow-input focus:shadow-input-focus appearance-none block w-full py-2 px-2 rounded',
            isCardElementReady ? 'h-auto' : 'h-[40px]',
          )}
        />

        {!isCardElementReady && (
          <div className="cp_stripe-card-element-loader absolute top-0 left-0 w-full h-full flex items-center justify-between pt-1 pointer-events-none">
            <div className="flex items-center gap-2">
              <div className="ml-2 h-4 w-6 bg-gray-200 rounded animate-pulse blur-[4px] opacity-70"></div>
              <div className="h-4 w-24 bg-gray-200 rounded animate-pulse blur-[4px] opacity-70"></div>
            </div>
            <div className="mr-6 h-4 w-28 bg-gray-200 rounded animate-pulse blur-[4px] opacity-70"></div>
          </div>
        )}
      </div>
    </>
  );
}

function MultiplePaymentMethods() {
  const { isMeasurer } = useCheckoutState();

  return <>{!isMeasurer && <PaymentElement />}</>;
}

export default PaymentMethods;
